import { QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { RouterProvider } from "react-router-dom";
import { Toaster } from "sonner";
import { getCurrentProduct, type ProductConfig } from "./config";
import { AppProvider } from "./lib/contexts/app-provider";
import { themeManager } from "./lib/theme-manager";
import { createTenantRoutes } from "./routes";
import { initializeLanguage, LanguageProvider } from "./lib/contexts/language-context";
import { queryClient } from "./lib/query-client";
import { ErrorBoundary } from "./components/error-boundary";

import "./styles/globals.css";
import { usePageMeta } from "./lib/hooks/use-meta";
import { api } from "@libs/api/client";
import type { ApiStaticData } from "@libs/api/types";

function App() {
  const [routes, setRoutes] = useState<any>(null);
  const [currentProduct, setCurrentProduct] = useState<ProductConfig | null>(null);
  const [systemInfo, setSystemInfo] =
    useState<ApiStaticData<"info", "get">>(null);

  usePageMeta({
    title: systemInfo?.system?.name || "Validador",
    favicon: systemInfo?.system?.icon || undefined,
    description: systemInfo?.system?.description || "Validador",
  });

  useEffect(() => {
    const product = getCurrentProduct();
    initializeLanguage(product.defaultLanguage);

    const getSystemInfo = async () => {
      const { data } = await api.info.get();
      setSystemInfo(data);
    };
    getSystemInfo();

    setCurrentProduct(product);

    const tenantRoutes = createTenantRoutes();
    setRoutes(tenantRoutes);

    themeManager.applyTheme(product);

    document.documentElement.setAttribute(
      "data-product",
      product.product.name.toLowerCase().replace(/\s+/g, ""),
    );
  }, []);

  if (!routes || !currentProduct) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p>Carregando configuração do produto...</p>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <AppProvider>
          <LanguageProvider defaultLanguage={currentProduct.defaultLanguage}>
            <RouterProvider router={routes} />
            <Toaster />
          </LanguageProvider>
        </AppProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
